/**
 * Relative-age fragment for a gateway ISO instant (ALL-5638).
 *
 * The wallet list renders freshness as `{formatAgo(balancesAsOf, now)} ago`, so
 * this returns the unit-bearing fragment only ("45s", "3m", "2h", "4d") and
 * `null` whenever there is nothing honest to show: a missing field, a blank or
 * unparseable string, or an instant in the future (a browser/gateway clock
 * skew — "0s ago" would be a claim about a read that has not happened yet).
 * Callers render `null` as the existing dash, never as "0s ago".
 */
export function formatAgo(iso: string | null | undefined, now: number | Date = Date.now()): string | null {
  if (typeof iso !== "string" || !iso.trim()) return null;
  // Date.parse collapses "42" and other non-instant strings to NaN, which is
  // the only reliable test here: `new Date("42")` is a valid Date object.
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return null;

  const nowMs = now instanceof Date ? now.getTime() : now;
  if (!Number.isFinite(nowMs)) return null;

  const seconds = Math.floor((nowMs - then) / 1000);
  if (seconds < 0) return null;
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  return `${Math.floor(hours / 24)}d`;
}
